import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/context/I18nContext";
import { api, unwrap } from "@/lib/api";

export const QuickQuoteBar = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [zip, setZip] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const go = async () => {
    const z = zip.trim();
    if (!/^\d{5}$/.test(z)) {
      setError(t("quick_quote.invalid"));
      return;
    }
    setError("");
    setLoading(true);
    try {
      const r = await api.post("/zipcode/check", { zip: z });
      const d = unwrap(r) || {};
      if (d.has_active_zone && d.zone_id) {
        navigate(`/book?zone_id=${encodeURIComponent(d.zone_id)}&step=2`);
      } else {
        const params = new URLSearchParams({
          zip: d.zip || z,
          city: d.city || "",
          state: d.state || "",
        });
        navigate(`/waitlist?${params.toString()}`);
      }
    } catch (err) {
      setError(err?.response?.data?.detail?.message || t("quick_quote.invalid"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border-b border-[#E5E7EB]" data-testid="quick-quote-bar">
      <div className="max-w-[720px] mx-auto px-4 md:px-8 py-7 text-center">
        <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tighter text-[#0C1A1A]" data-testid="quick-quote-title">
          {t("quick_quote.title")}
        </h2>
        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <Input
            value={zip}
            onChange={(e) => { setZip(e.target.value.replace(/[^0-9]/g, "").slice(0, 5)); setError(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") go(); }}
            inputMode="numeric"
            placeholder={t("quick_quote.placeholder")}
            aria-label={t("quick_quote.placeholder")}
            className="flex-1 h-14 rounded-2xl text-base px-5 font-mono tracking-wider border-[#E5E7EB] focus-visible:ring-[#0EA5A4]"
            data-testid="quick-quote-input"
          />
          <Button
            onClick={go}
            disabled={loading}
            className="h-14 px-8 rounded-2xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-base font-semibold shadow-lg shadow-[#0EA5A4]/20 hover:scale-[1.02] transition-transform shrink-0"
            data-testid="quick-quote-go"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t("quick_quote.go")} <ArrowRight className="ml-1.5 w-5 h-5" /></>}
          </Button>
        </div>
        {error && (
          <div className="mt-2 text-sm text-rose-600 text-left" data-testid="quick-quote-error">{error}</div>
        )}
        <p className="mt-3 text-sm text-[#9CA3AF] flex items-center justify-center gap-1.5" data-testid="quick-quote-microcopy">
          <MapPin className="w-3.5 h-3.5" /> {t("quick_quote.microcopy")}
        </p>
      </div>
    </div>
  );
};
